import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from './Navbar';
import Footer from './Footer';

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Terms &amp; Conditions | Clarendon Family Medicine</title>
        <meta name="description" content="Terms and conditions for Clarendon Family Medicine, including our SMS patient communication program opt-in, opt-out, and data practices." />
        <meta property="og:title" content="Terms &amp; Conditions | Clarendon Family Medicine" />
        <meta property="og:url" content="https://www.clarendon.clinic/termsandconditions" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.clarendon.clinic/termsandconditions" />
      </Helmet>
      <Navbar />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="mb-10 pb-8 border-b border-sky-100">
            <p className="text-sky-500 font-semibold text-sm uppercase tracking-widest mb-3">Legal</p>
            <h1
              data-testid="terms-title"
              className="text-4xl sm:text-5xl font-bold text-sky-900 mb-4"
              style={{fontFamily: 'Outfit, sans-serif'}}
            >
              Terms &amp; Conditions
            </h1>
            <p className="text-sky-600 text-sm">
              <strong>Clarendon Family Medicine</strong> &nbsp;|&nbsp; Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Body */}
          <div className="space-y-8 text-sky-800 text-base leading-relaxed" data-testid="terms-content">

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>1. Agreement to Terms</h2>
              <p>
                These Terms and Conditions ("Terms") govern your use of the website located at <strong>www.Clarendon.clinic</strong> and the SMS patient communication services operated by Clarendon Family Medicine ("we," "us," or "our"), located at One Medical Center Drive, Clarendon, TX 79226. By accessing our website or enrolling in our SMS messaging program, you agree to be bound by these Terms. If you do not agree, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>2. SMS Patient Communication Program</h2>

              <h3 className="text-lg font-semibold text-sky-800 mt-5 mb-2">2.1 Program Name &amp; Description</h3>
              <p>
                <strong>Program Name:</strong> Clarendon Family Medicine Patient Communication
              </p>
              <p className="mt-2">
                Clarendon Family Medicine operates an SMS-based patient communication program to send enrolled patients important health-related information, including but not limited to: appointment reminders, care instructions, follow-up notifications, health alerts, and general clinic communications. This program is operated for the benefit of our patients and is not used for marketing or promotional purposes unrelated to your healthcare.
              </p>

              <h3 className="text-lg font-semibold text-sky-800 mt-5 mb-2">2.2 How to Opt In</h3>
              <p>
                Enrollment in the Clarendon Family Medicine Patient Communication program is voluntary. To enroll, patients must sign a written consent form at our clinic prior to receiving any SMS messages. We will not send you SMS messages without your prior explicit written consent.
              </p>

              <h3 className="text-lg font-semibold text-sky-800 mt-5 mb-2">2.3 Message Frequency</h3>
              <p>
                Message frequency varies and is based on your care schedule and clinic communications. You may receive messages related to upcoming appointments, care reminders, clinic notices, or other health-related updates. On average, you can expect to receive a variable number of messages per month depending on your care activity.
              </p>

              <h3 className="text-lg font-semibold text-sky-800 mt-5 mb-2">2.4 Message &amp; Data Rates</h3>
              <p>
                <strong>Message and data rates may apply.</strong> The cost of SMS messages sent and received through this program depends on your mobile carrier plan. Clarendon Family Medicine does not charge any additional fee for participating in the SMS program. Please check with your wireless carrier for details about your messaging plan.
              </p>

              <h3 className="text-lg font-semibold text-sky-800 mt-5 mb-2">2.5 How to Opt Out</h3>
              <p>
                You may opt out of receiving SMS messages from Clarendon Family Medicine at any time. To stop receiving messages, use any of the following methods:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2 text-sky-700">
                <li>Text the word <strong>STOP</strong> in reply to any message you receive from us. Upon receipt of your STOP request, we will send you one final confirmation message and you will be removed from the program immediately.</li>
                <li>Notify our office staff in person during your visit.</li>
                <li>Call us at <strong>(806) 874-3531</strong> and request to be removed from the SMS program.</li>
              </ul>
              <p className="mt-3">
                After opting out, you will not receive any further SMS communications unless you provide written consent again in the future.
              </p>

              <h3 className="text-lg font-semibold text-sky-800 mt-5 mb-2">2.6 How to Get Help</h3>
              <p>
                If you need assistance with the SMS program or have questions, you may:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2 text-sky-700">
                <li>Text <strong>HELP</strong> in reply to any SMS message you receive from us. We will respond with support information.</li>
                <li>Call our office at <strong>(806) 874-3531</strong> during regular office hours.</li>
                <li>Visit us at One Medical Center Drive, Clarendon, TX 79226.</li>
              </ul>
              <p className="mt-3">
                <strong>Support Contact:</strong> Clarendon Family Medicine — (806) 874-3531 — One Medical Center Drive, Clarendon, TX 79226
              </p>

              <h3 className="text-lg font-semibold text-sky-800 mt-5 mb-2">2.7 Privacy of SMS Data</h3>
              <p>
                Your mobile number and SMS consent information are kept confidential. <strong>No mobile information obtained through this program will be shared with third parties for marketing or promotional purposes.</strong> All data is handled in accordance with our Privacy Policy and applicable laws, including HIPAA.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>3. Website Use</h2>
              <p>
                Our website at <strong>www.Clarendon.clinic</strong> is provided for informational purposes only. The content on this website does not constitute medical advice and is not a substitute for professional medical consultation, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider for any medical condition.
              </p>
              <p className="mt-3">
                You agree to use our website only for lawful purposes and in a manner that does not infringe the rights of others or restrict their use of the website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>4. Intellectual Property</h2>
              <p>
                All content on this website, including text, graphics, logos, images, and design elements, is the property of Clarendon Family Medicine and is protected by applicable copyright and trademark laws. You may not reproduce, distribute, or use any content from this website without our prior written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>5. Limitation of Liability</h2>
              <p>
                Clarendon Family Medicine shall not be liable for any direct, indirect, incidental, consequential, or special damages arising out of or related to your use of our website or SMS communications, including but not limited to reliance on any information provided on the website. Our website and SMS program are provided "as is" without warranties of any kind.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>6. HIPAA &amp; Medical Information</h2>
              <p>
                Clarendon Family Medicine is a covered entity under HIPAA. All medical information shared with us is subject to our HIPAA Notice of Privacy Practices. SMS messages sent through our patient communication program may include general health reminders and appointment information; however, detailed protected health information (PHI) will not be transmitted via SMS without your authorization.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>7. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Donley County, Texas.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>8. Changes to These Terms</h2>
              <p>
                We reserve the right to update or modify these Terms at any time. Changes will be effective upon posting to this page with an updated date. Your continued use of our website or SMS program after any changes constitutes your acceptance of the revised Terms. We encourage you to review these Terms periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-sky-900 mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>9. Contact Information</h2>
              <p>For questions about these Terms, our SMS program, or to opt out, please contact us:</p>
              <div className="mt-4 bg-sky-50 border border-sky-100 rounded-2xl p-6">
                <p className="font-bold text-sky-900 text-lg mb-2">Clarendon Family Medicine</p>
                <p className="text-sky-700">One Medical Center Drive</p>
                <p className="text-sky-700">Clarendon, TX 79226</p>
                <p className="text-sky-700 mt-2">
                  Phone: <a href="tel:8068743531" className="text-sky-600 font-semibold hover:underline">(806) 874-3531</a>
                </p>
                <p className="text-sky-700 mt-2">
                  <strong>SMS Help:</strong> Text <strong>HELP</strong> to our SMS number &nbsp;|&nbsp; <strong>To Opt Out:</strong> Text <strong>STOP</strong>
                </p>
              </div>
            </section>

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
